import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./tooltips.module.css";
import { TooltipPosition } from "./tooltip-registry";
import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";

interface TooltipProps {
  content: string;
  position: TooltipPosition;
  targetElement: HTMLElement | null;
  onDismiss: () => void;
  isVisible: boolean;
}

interface TooltipCoords {
  top: number;
  left: number;
}

const ARROW_SIZE = 23;
const OFFSET = 12;

function calculatePosition(
  target: HTMLElement,
  position: TooltipPosition,
  tooltipWidth: number,
  tooltipHeight: number
): TooltipCoords {
  const rect = target.getBoundingClientRect();
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  let top = 0;
  let left = 0;

  switch (position) {
    case "top":
      top = rect.top + scrollY - tooltipHeight - OFFSET;
      left = rect.left + scrollX + rect.width / 2 - tooltipWidth / 2;
      break;
    case "bottom":
      top = rect.bottom + scrollY + OFFSET;
      left = rect.left + scrollX + rect.width / 2 - tooltipWidth / 2;
      break;
    case "left":
      top = rect.top + scrollY + rect.height / 2 - tooltipHeight / 2;
      left = rect.left + scrollX - tooltipWidth - OFFSET;
      break;
    case "right":
      top = rect.top + scrollY + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + scrollX + OFFSET;
      break;
  }

  // Keep tooltip within viewport bounds
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  if (left < 10) left = 10;
  if (left + tooltipWidth > viewportWidth - 10) {
    left = viewportWidth - tooltipWidth - 10;
  }
  if (top < 10) top = 10;
  if (top + tooltipHeight > viewportHeight - 10) {
    top = viewportHeight - tooltipHeight - 10;
  }

  return { top, left };
}

export function Tooltip({
  content,
  position,
  targetElement,
  onDismiss,
  isVisible,
}: TooltipProps) {
  const [coords, setCoords] = useState<TooltipCoords>({ top: 0, left: 0 });
  const [tooltipSize, setTooltipSize] = useState({ width: 240, height: 60 });

  const updatePosition = useCallback(() => {
    if (targetElement && isVisible) {
      const newCoords = calculatePosition(
        targetElement,
        position,
        tooltipSize.width,
        tooltipSize.height
      );
      setCoords(newCoords);
    }
  }, [targetElement, position, tooltipSize, isVisible]);

  // Update position when target or visibility changes
  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isVisible) return;

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isVisible, updatePosition]);

  // Measure tooltip size after render
  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        const { width, height } = node.getBoundingClientRect();
        if (width !== tooltipSize.width || height !== tooltipSize.height) {
          setTooltipSize({ width, height });
        }
      }
    },
    [tooltipSize]
  );

  const getArrowClass = () => {
    switch (position) {
      case "top":
        return styles.arrowBottom;
      case "bottom":
        return styles.arrowTop;
      case "left":
        return styles.arrowRight;
      case "right":
        return styles.arrowLeft;
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isVisible && targetElement && (
        <motion.div
          ref={measureRef}
          className={styles.tooltip}
          style={{
            top: coords.top,
            left: coords.left,
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`${styles.arrow} ${getArrowClass()}`} />
          <div className={styles.textContainer}>
            <UIIcon iconName="BULB" variant="SELECTED" />
            <p className={styles.content}>{content}</p>
          </div>
          <button
            className={styles.dismissButton}
            onClick={onDismiss}
            aria-label="Dismiss tooltip"
          >
            Got it
          </button>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

// Floating tooltip for map canvas (not attached to a specific element)
interface FloatingTooltipProps {
  content: string;
  position: "left-of-center";
  containerRef: React.RefObject<HTMLElement>;
  onDismiss: () => void;
  isVisible: boolean;
}

export function FloatingTooltip({
  content,
  containerRef,
  onDismiss,
  isVisible,
}: FloatingTooltipProps) {
  const [coords, setCoords] = useState<TooltipCoords>({ top: 0, left: 0 });

  useEffect(() => {
    if (containerRef.current && isVisible) {
      const rect = containerRef.current.getBoundingClientRect();
      // Position tooltip to the left of center, pointing right toward the map
      setCoords({
        top: rect.top + rect.height / 2 - 30, // Vertically centered
        left: rect.left + 20, // 20px from left edge
      });
    }
  }, [containerRef, isVisible]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={styles.tooltip}
          style={{
            top: coords.top,
            left: coords.left,
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`${styles.arrow} ${styles.arrowRight}`} />
          <div className={styles.textContainer}>
            <UIIcon iconName="BULB" variant="SELECTED" />
            <p className={styles.content}>{content}</p>
          </div>
          <button
            className={styles.dismissButton}
            onClick={onDismiss}
            aria-label="Dismiss tooltip"
          >
            Got it
          </button>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
